import { applyFilters } from 'helpers/filterData.helper'
import {
  IFilterData,
  IFilterDataArray,
  IFilterGroupedData,
} from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import { InputKey, Value, ValueBase } from 'components/monitoring/types'
import { IFilterDataDynamic, IFilterDataArrayDynamic } from '../../types'

export const getPercentage = (number: number, total: number) =>
  Math.round((number / (total || 1)) * 100)

interface ExtractFiltersOpts<
  Type extends InputKey,
  Keys extends InputKey = string
> {
  type?: Type
  maps?: Partial<Record<Keys, string | string[]>>
}

function dataPush<Type extends InputKey>({
  filter,
  dataInput,
  type,
}: {
  filter: IFilterDataArrayDynamic<Type>
  dataInput: ValueBase<Type>
  type: Type
}): IFilterDataArray[] {
  if (typeof dataInput === 'function') {
    const data = dataInput(type, filter)
    if (Array.isArray(data))
      return data.flatMap((key) => dataPush({ filter, dataInput: key, type }))
    return dataPush({ filter, dataInput: data, type })
  }
  // eslint-disable-next-line
  const { input: _input, ...rest } = filter
  return [
    {
      ...rest,
      ...(typeof dataInput === 'string' ? { input: dataInput } : dataInput),
    },
  ]
}

export const transformData = <Type extends InputKey, Keys extends InputKey>(
  dataDynamic: IFilterDataArrayDynamic<Type>[],
  opts?: ExtractFiltersOpts<Type, Keys>
) => {
  const { type, maps } = { ...opts }
  const data: IFilterDataArray[] = []
  dataDynamic.forEach((filter) => {
    const { input } = filter

    if (typeof input === 'object') {
      const children: Value<Type> | undefined | null =
        type &&
        (typeof input.children === 'function'
          ? input.children(type)
          : input.children[type])
      if (type && children) {
        const dataInput: Value<Type> =
          (typeof children === 'string' && maps?.[children]) || children
        if (Array.isArray(dataInput))
          dataInput.forEach((key) =>
            data.push(...dataPush<Type>({ filter, dataInput: key, type }))
          )
        else if (dataInput)
          data.push(...dataPush<Type>({ filter, dataInput: dataInput, type }))
        return
      }
    } else data.push({ ...filter, input: (input && maps?.[input]) || input })
  })
  return data
}

export function transformFilterData<
  Type extends InputKey,
  Keys extends InputKey = string
>(
  filterData?: IFilterDataDynamic<Type>,
  opts?: ExtractFiltersOpts<Type, Keys>
) {
  const { data, groupedData, ...rest } = { ...filterData }
  const newFilter: IFilterData | undefined = filterData && { ...rest }
  if (newFilter) {
    if (data) newFilter.data = transformData<Type, Keys>(data, opts)
    if (groupedData) {
      const newGroupedData = groupedData.map((group): IFilterGroupedData => {
        const { data, ...rest } = group
        if (data) {
          const filter = transformData<Type, Keys>(data, opts)
          return {
            ...group,
            data: filter,
          }
        }
        return { ...rest }
      })
      newFilter.groupedData = newGroupedData
    }
  }
  return { filters: applyFilters(newFilter) }
}
